import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, map, of, tap } from 'rxjs';
import { ApiService } from './api.service';
import { isNumericProjectRouteRef, projectLink, projectRouteSegment, slugifyProjectName } from '../utils/project-route.util';

export type ProjectRouteMeta = { id: number; name: string; project_key: string; avatar_url?: string | null };

@Injectable({ providedIn: 'root' })
export class ProjectRouteService {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);

  readonly slug = signal<string | null>(null);
  readonly projectId = signal<number | null>(null);
  readonly meta = signal<ProjectRouteMeta | null>(null);
  readonly resolving = signal(false);
  readonly resolveError = signal(false);

  static segment = projectRouteSegment;
  static link = projectLink;
  static slugify = slugifyProjectName;

  /** Resolve route ref (slug, project key, or legacy numeric id) to project id. */
  ensureResolved(ref: string): Observable<number | null> {
    const raw = String(ref ?? '').trim();
    if (!raw) return of(null);
    const cachedId = this.projectId();
    const cachedSlug = this.slug();
    if (cachedSlug === raw && cachedId != null && cachedId > 0) {
      return of(cachedId);
    }
    if (cachedSlug !== raw) {
      this.slug.set(null);
      this.projectId.set(null);
      this.meta.set(null);
    }

    this.resolving.set(true);
    this.resolveError.set(false);
    return this.api
      .get<ProjectRouteMeta & { project?: ProjectRouteMeta }>('/projects/' + encodeURIComponent(raw))
      .pipe(
        map((res) => {
          if (!res.status || !res.data) {
            return null;
          }
          const row = (res.data.project ?? res.data) as ProjectRouteMeta;
          const id = Number(row.id);
          if (!Number.isFinite(id) || id <= 0) return null;
          const meta: ProjectRouteMeta = {
            id,
            name: String(row.name ?? ''),
            project_key: String(row.project_key ?? ''),
            avatar_url: row.avatar_url ?? null,
          };
          this.slug.set(raw);
          this.projectId.set(id);
          this.meta.set(meta);
          return id;
        }),
        tap(() => this.resolving.set(false)),
        tap((id) => {
          if (id == null) this.resolveError.set(true);
        }),
      );
  }

  /** Replace URL with canonical name slug while keeping child path and query. */
  replaceUrlWithCanonicalSlug(currentRef: string): void {
    const meta = this.meta();
    if (!meta) return;
    const canonical = projectRouteSegment(meta);
    if (currentRef === canonical) return;
    const pathAndQuery = this.router.url.replace(/^\/projects\/[^/]+/, '');
    const segments = pathAndQuery.split('?')[0].split('/').filter(Boolean);
    void this.router.navigate(['/projects', canonical, ...segments], {
      replaceUrl: true,
      queryParamsHandling: 'preserve',
    });
  }

  linkFromMeta(meta: ProjectRouteMeta | null, ...parts: string[]): string[] {
    if (!meta) return ['/projects'];
    return projectLink(meta, ...parts);
  }

  linkFromId(id: number, _name: string, ...parts: string[]): string[] {
    return ['/projects', String(id), ...parts];
  }

  parseActiveProjectRefFromUrl(url: string): string | null {
    const m = url.match(/\/projects\/([^/]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  isNumericRef = isNumericProjectRouteRef;
}
